import React from 'react'

import {flatMap} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import {useNavigate} from 'react-router'

import {useAppDispatch} from '../../hooks/redux'
import {fatalError} from '../../redux/features/core'

import useAsync from '../../../../commons/src/hooks/use-async'
import * as optionApi from '../../services/api/option'
import * as providerInfoApi from '../../services/api/provider-info'
import {goToEditProviderInfo} from '../../services/navigation'
import {MixedValue, isMixedValue, toMixedValidationSchema, toTouchedFormData} from '../utils/form'

import {EditProviderInfoTab, OptionId, ProviderId, ProviderInfo} from '../../types'

import SaveButtonPartial from '../save-button-partial'

import PrintingMethodFormPartial, {
  MixedFormData,
  SingleFormData,
  formDataToPartialRequest,
  formatInfillName,
  initialValues,
  printingMethodToMixedFormData,
  sortInfills,
  updateLinkedInfills,
  validationSchema,
} from './printing-method-form-partial'

type Props = {
  providerId: ProviderId
  providerInfos: Array<ProviderInfo>
}

const EditPrintingMethodPartial = ({providerId, providerInfos}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const {
    isLoading: isInfillsLoading,
    data: infills = [],
    refresh: refreshInfills,
  } = useAsync(() => optionApi.getInfillsWithLinkedProviderInfosByProvider(providerId), [], {
    onError: error => dispatch(fatalError({error})),
  })

  const infillOptions = sortInfills(infills).map(infill => ({
    label: formatInfillName(infill),
    value: infill.id,
  }))

  const formik = useFormik<MixedFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...printingMethodToMixedFormData(providerInfos, sortInfills(infills)),
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(values, initialValues) as Partial<SingleFormData>
      const providerInfoRequest = formDataToPartialRequest(touchedValues)

      try {
        const providerInfoIds = providerInfos.map(providerInfo => providerInfo.id)

        if (touchedValues.infillIds) {
          // Treat cases differently where infills were initially a mixed value
          // in order to be able to find out their correct initial value.
          if (!isMixedValue(formik.initialValues.infillIds)) {
            const initialInfillIds = formik.initialValues.infillIds

            await updateLinkedInfills(
              initialInfillIds,
              touchedValues.infillIds as OptionId[],
              providerInfoIds
            )
          } else {
            const updateLinkedInfillPromises = flatMap(
              providerInfos,
              async (providerInfo: ProviderInfo, index: number) => {
                const initialInfillIds = (formik.initialValues.infillIds as MixedValue<OptionId[]>)
                  .values[index]

                return updateLinkedInfills(
                  initialInfillIds,
                  touchedValues.infillIds as OptionId[],
                  [providerInfo.id]
                )
              }
            )

            await Promise.all(updateLinkedInfillPromises)
          }

          refreshInfills()
        }

        const nextProviderInfos = await Promise.all(
          providerInfos.map(providerInfo =>
            providerInfoApi.patch(
              {
                ...providerInfoRequest,
                // If we change the provider info the API expects the materialConfigId otherwise we get an error.
                materialConfigId: providerInfo.materialConfigId,
              },
              providerInfo.id
            )
          )
        )

        goToEditProviderInfo(navigate, {
          providerInfos: nextProviderInfos,
          activeTab: EditProviderInfoTab.PRINTING_METHOD,
          replace: true,
        })

        setIsSubmitted(true)
      } catch (error) {
        if ((error as Error).message === 'Unallowed Relation Expression') {
          actions.setFieldError(
            'formikGeneral',
            'Infill options are only supported for FDM configurations'
          )
        } else {
          actions.setFieldError('formikGeneral', (error as Error).message)
        }
      }
    },
  })

  return (
    <PrintingMethodFormPartial
      formik={formik}
      isLoading={isInfillsLoading}
      onRefresh={refreshInfills}
      submitButton={<SaveButtonPartial formik={formik} isSubmitted={isSubmitted} />}
      infillOptions={infillOptions}
      providerInfos={providerInfos}
    />
  )
}

export default EditPrintingMethodPartial
